import { useMutation } from "@apollo/client/react";
import { CheckOutlined } from "@ant-design/icons";
import { css } from "@linaria/core";
import { Alert, App, Button, Flex, Form, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useCurrentSession } from "../../context/CurrentSessionContext.tsx";
import { UpdateTeamSettingsDocument } from "../../gql/generated/graphqlSchema.tsx";
import { useServiceError } from "../../hooks/useServiceError.ts";
import { formNarrow } from "../../styles/shared.styles.ts";

const CURRENCIES = ["EUR", "BGN", "USD", "GBP", "CHF", "CZK", "PLN", "RON", "HUF", "DKK", "SEK", "NOK"];

// EU member states (ISO 3166-1 alpha-2)
const COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

const alertStyle = css`
  margin-bottom: var(--ant-margin);
`;

type FormValues = {
  currency: string;
  country: string;
};

export const TeamSettingsForm = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const { team, refetchTeam } = useCurrentSession();

  const [updateSettings, { loading, error }] = useMutation(UpdateTeamSettingsDocument, {
    onCompleted: () => {
      void message.success(t("team.settings.saveDone"));
      void refetchTeam();
    },
  });
  const errorMessage = useServiceError(error);

  if (team == null) {
    return null;
  }

  const regionNames = new Intl.DisplayNames([i18n.language], { type: "region" });
  const countryOptions = COUNTRIES.map((code) => ({ value: code, label: regionNames.of(code) ?? code })).sort((a, b) =>
    a.label.localeCompare(b.label, i18n.language),
  );

  const handleFinish = (values: FormValues) => {
    void updateSettings({ variables: { input: { currency: values.currency, country: values.country } } });
  };

  return (
    <Flex gap="small" vertical>
      <Form
        className={formNarrow}
        form={form}
        initialValues={{ currency: team.currency, country: team.country }}
        layout="vertical"
        onFinish={handleFinish}
      >
        <Form.Item label={t("team.settings.currency")} name="currency">
          <Select options={CURRENCIES.map((c) => ({ value: c, label: c }))} showSearch />
        </Form.Item>
        <Form.Item label={t("team.settings.country")} name="country">
          <Select options={countryOptions} showSearch={{ optionFilterProp: "label" }} />
        </Form.Item>
        {errorMessage != null ? <Alert className={alertStyle} showIcon title={errorMessage} type="error" /> : null}
        <Button htmlType="submit" icon={<CheckOutlined />} loading={loading} type="primary">
          {t("common.save")}
        </Button>
      </Form>
    </Flex>
  );
};
